import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'

export interface EmergencyContact {
    firstName: string
    lastName: string
    phoneNumber: string
    relationship: string
}

interface EmergencyContactPayload {
    first_name: string
    last_name: string
    phone_number: string | null
    relationship: string | null
}

const toPayload = ({ firstName, lastName, phoneNumber, relationship }: EmergencyContact): EmergencyContactPayload => ({
    first_name: firstName,
    last_name: lastName,
    phone_number: phoneNumber,
    relationship,
})

const toEmergencyContacts = (response?: { emergency_contacts?: EmergencyContactPayload[] | null }): EmergencyContact[] =>
    (response?.emergency_contacts ?? []).map((contact) => ({
        firstName: contact.first_name,
        lastName: contact.last_name,
        phoneNumber: contact.phone_number ?? '',
        relationship: contact.relationship ?? '',
    }))

// Mercy identifies a contact by name and relationship, so saving a contact that matches a held one updates it.
const contactIdentity = ({ firstName, lastName, relationship }: EmergencyContact) =>
    [firstName, lastName, relationship].map((value) => value.trim().toLowerCase()).join('|')

export const emergencyContactsApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getEmergencyContacts: builder.query<EmergencyContact[], void>({
            query: () => apiEndpoints.emergencyContacts.list,
            transformResponse: toEmergencyContacts,
            providesTags: ['EmergencyContacts'],
        }),
        addEmergencyContact: builder.mutation<EmergencyContact[], EmergencyContact>({
            query: (emergencyContact) => ({
                url: apiEndpoints.emergencyContacts.list,
                method: 'POST',
                body: { emergency_contact: toPayload(emergencyContact) },
            }),
            transformResponse: toEmergencyContacts,
            // Mercy applies contact updates asynchronously, so a re-read straight after a write returns the old
            // contacts, and the response is built from such a read. The saved contact is added to the cached list
            // instead; with no list cached yet, the response seeds it together with the saved contact.
            onQueryStarted: async (emergencyContact, { dispatch, queryFulfilled }) => {
                // A rejected save leaves the cached list as it was; the caller handles the error.
                const savedResponse = await queryFulfilled.catch(() => undefined)
                if (!savedResponse) return

                let hasCachedList = false
                dispatch(
                    emergencyContactsApi.util.updateQueryData('getEmergencyContacts', undefined, (cachedContacts) => {
                        hasCachedList = true
                        const index = cachedContacts.findIndex(
                            (cachedContact) => contactIdentity(cachedContact) === contactIdentity(emergencyContact)
                        )
                        if (index === -1) cachedContacts.push(emergencyContact)
                        else cachedContacts[index] = emergencyContact
                    })
                )
                if (!hasCachedList) {
                    const seededContacts = [
                        ...savedResponse.data.filter(
                            (contact) => contactIdentity(contact) !== contactIdentity(emergencyContact)
                        ),
                        emergencyContact,
                    ]
                    dispatch(emergencyContactsApi.util.upsertQueryData('getEmergencyContacts', undefined, seededContacts))
                }
            },
        }),
        // Replaces the patient's whole list, which is how contacts are edited.
        // Mercy rejects a contact without a phone number, so a contact held without one is never sent back.
        replaceEmergencyContacts: builder.mutation<EmergencyContact[], EmergencyContact[]>({
            query: (emergencyContacts) => ({
                url: apiEndpoints.emergencyContacts.list,
                method: 'PUT',
                body: {
                    emergency_contacts: emergencyContacts
                        .filter(({ phoneNumber }: EmergencyContact) => Boolean(phoneNumber.trim()))
                        .map(toPayload),
                },
            }),
            transformResponse: toEmergencyContacts,
            // A re-read straight after the write would return the old list, so the cached list is set to the
            // submitted one instead of being invalidated.
            onQueryStarted: async (emergencyContacts, { dispatch, queryFulfilled }) => {
                // A rejected save leaves the cached list as it was; the caller handles the error.
                const savedResponse = await queryFulfilled.catch(() => undefined)
                if (!savedResponse) return

                dispatch(emergencyContactsApi.util.upsertQueryData('getEmergencyContacts', undefined, emergencyContacts))
            },
        }),
    }),
    overrideExisting: false,
})

export const { useGetEmergencyContactsQuery, useAddEmergencyContactMutation, useReplaceEmergencyContactsMutation } = emergencyContactsApi
